/**
 * Persists and applies Shopify discount codes without full page reload.
 */
(function () {
  const STORAGE_KEY = 'mp_discount_code';
  const CLEAR_CODE = '__MP_REMOVE_DISCOUNT__';

  function normalizeCode(code) {
    return (code || '').trim();
  }

  function getDiscountFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return normalizeCode(params.get('discount') || params.get('discount_code'));
  }

  function getStoredCode() {
    return sessionStorage.getItem(STORAGE_KEY);
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then((response) => response.json());
  }

  function cartHasAnyDiscount(cart) {
    if ((cart.cart_level_discount_applications || []).length > 0) return true;
    return (cart.items || []).some((item) => (item.line_level_discount_allocations || []).length > 0);
  }

  function cartHasDiscountCode(cart, code) {
    const upper = code.toUpperCase();
    const cartLevel = (cart.cart_level_discount_applications || []).some(
      (discount) => (discount.title || '').toUpperCase() === upper
    );
    if (cartLevel) return true;

    return (cart.items || []).some((item) =>
      (item.line_level_discount_allocations || []).some(
        (allocation) => (allocation.discount_application?.title || '').toUpperCase() === upper
      )
    );
  }

  function showDiscountError(message) {
    const errorEl = document.getElementById('CartDrawer-Discount-Error');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function hideDiscountError() {
    const errorEl = document.getElementById('CartDrawer-Discount-Error');
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  function setApplyButtonLoading(isLoading) {
    const applyBtn = document.getElementById('CartDrawer-Discount-Apply');
    if (!applyBtn) return;
    applyBtn.disabled = isLoading;
    applyBtn.textContent = isLoading ? 'Applying…' : 'Apply';
  }

  function setRemoveButtonLoading(isLoading) {
    const removeBtn = document.getElementById('CartDrawer-Discount-Remove');
    if (removeBtn) {
      removeBtn.disabled = isLoading;
      removeBtn.textContent = isLoading ? 'Removing…' : 'Remove';
    }

    document.querySelectorAll('.cart-drawer__discount-remove--pill').forEach((btn) => {
      btn.disabled = isLoading;
    });
  }

  function clearDiscountSession() {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  function visitDiscountUrl(code) {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
      iframe.onload = () => {
        setTimeout(() => {
          iframe.remove();
          resolve();
        }, 500);
      };
      iframe.src = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(window.location.pathname)}`;
      document.body.appendChild(iframe);
    });
  }

  async function refreshDrawer() {
    if (typeof window.refreshCartDrawer === 'function') {
      await window.refreshCartDrawer();
    }
  }

  async function removeDiscountAjax() {
    hideDiscountError();
    setRemoveButtonLoading(true);
    clearDiscountSession();

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: '' }),
      });

      await visitDiscountUrl(CLEAR_CODE);

      await refreshDrawer();

      const input = document.getElementById('CartDrawer-Discount');
      if (input) {
        input.value = '';
        input.removeAttribute('readonly');
      }
    } catch (error) {
      console.error('Discount remove failed:', error);
      showDiscountError('Could not remove discount code. Please try again.');
    } finally {
      setRemoveButtonLoading(false);
    }
  }

  async function applyDiscountAjax(code) {
    const normalized = normalizeCode(code);
    if (!normalized) return;

    hideDiscountError();
    setApplyButtonLoading(true);

    try {
      const currentCart = await fetchCart();
      if (cartHasDiscountCode(currentCart, normalized)) {
        sessionStorage.setItem(STORAGE_KEY, normalized.toUpperCase());
        await refreshDrawer();
        return;
      }

      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: normalized }),
      });

      if (!updateResponse.ok) {
        throw new Error('Cart update failed');
      }

      await visitDiscountUrl(normalized);
      await refreshDrawer();

      const updatedCart = await fetchCart();
      if (!cartHasDiscountCode(updatedCart, normalized) && !cartHasAnyDiscount(updatedCart)) {
        clearDiscountSession();
        showDiscountError('Invalid or expired discount code.');
        return;
      }

      sessionStorage.setItem(STORAGE_KEY, normalized.toUpperCase());
    } catch (error) {
      console.error('Discount apply failed:', error);
      showDiscountError('Could not apply discount code. Please try again.');
    } finally {
      setApplyButtonLoading(false);
    }
  }

  window.ensureCartDiscount = function ensureCartDiscount() {
    const code = getStoredCode();
    if (!code) return Promise.resolve();

    return fetchCart().then((cart) => {
      if (cartHasDiscountCode(cart, code)) return Promise.resolve();

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code }),
      })
        .then(() => visitDiscountUrl(code))
        .then(refreshDrawer);
    });
  };

  function initDiscountInput() {
    if (document.getElementById('CartDrawer-Discount-Remove')) {
      hideDiscountError();
    }
  }

  function initFromUrl() {
    const code = getDiscountFromUrl();
    if (!code) return false;

    const url = new URL(window.location.href);
    url.searchParams.delete('discount');
    url.searchParams.delete('discount_code');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);

    applyDiscountAjax(code);
    return true;
  }

  document.addEventListener('click', (event) => {
    const removeBtn = event.target.closest('.cart-drawer__discount-remove');
    if (removeBtn) {
      event.preventDefault();
      removeDiscountAjax();
      return;
    }

    const applyBtn = event.target.closest('.cart-drawer__discount-apply');
    if (!applyBtn) return;

    event.preventDefault();
    const input = document.getElementById('CartDrawer-Discount');
    if (input && normalizeCode(input.value)) {
      applyDiscountAjax(input.value);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const input = document.getElementById('CartDrawer-Discount');
    if (event.target !== input || input.hasAttribute('readonly')) return;

    event.preventDefault();
    if (normalizeCode(input.value)) {
      applyDiscountAjax(input.value);
    }
  });

  function boot() {
    if (!initFromUrl()) {
      initDiscountInput();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('cart:refresh', initDiscountInput);
})();
